import { Card } from "@/components/ui/card";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export type GradeRange = '1-4' | '5-6' | '7-8' | '9';

export const getDifficultyNumbers = (ranges: GradeRange[]): number[] => {
  const result: number[] = [];
  if (ranges.includes('1-4')) result.push(1, 2, 3, 4);
  if (ranges.includes('5-6')) result.push(5, 6);
  if (ranges.includes('7-8')) result.push(7, 8);
  if (ranges.includes('9')) result.push(9);
  return result;
};

export const getAvailableGrades = (questions: { difficulty: number }[]): GradeRange[] => {
  const grades = new Set<GradeRange>();
  for (const q of questions) {
    if (q.difficulty >= 1 && q.difficulty <= 4) grades.add('1-4');
    else if (q.difficulty === 5 || q.difficulty === 6) grades.add('5-6');
    else if (q.difficulty === 7 || q.difficulty === 8) grades.add('7-8');
    else if (q.difficulty === 9) grades.add('9');
  }
  return Array.from(grades);
};

interface DifficultySelectorProps {
  selected: GradeRange[];
  availableGrades?: GradeRange[];
  onChange: (difficulty: GradeRange[]) => void;
}

export default function DifficultySelector({ selected, availableGrades, onChange }: DifficultySelectorProps) {
  const options: { value: GradeRange; label: string; desc: string }[] = [
    { value: '1-4', label: 'Grades 1-4', desc: 'Foundation tier fundamentals' },        
    { value: '5-6', label: 'Grades 5-6', desc: 'Mid-level GCSE challenge' },
    { value: '7-8', label: 'Grades 7-8', desc: 'Higher tier advanced' },
    { value: '9', label: 'Grade 9', desc: 'Top level mastery' }
  ];

  const handleToggle = (val: GradeRange) => {
    if (selected.includes(val)) {
      if (selected.length > 1) {
        onChange(selected.filter(v => v !== val));
      }
    } else {
      onChange([...selected, val]);
    }
  };

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-muted-foreground">Select Target Grades (Multi-select)</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {options.map((opt) => {
          const isSelected = selected.includes(opt.value);
          const isDisabled = availableGrades && availableGrades.length > 0 && !availableGrades.includes(opt.value);
          return (
          <Card
            key={opt.value}
            className={cn(
              "p-4 transition-all",
              isDisabled ? "opacity-50 cursor-not-allowed bg-muted/50" : "cursor-pointer hover-elevate active-elevate-2 hover:border-primary/50",       
              isSelected && !isDisabled ? "border-primary bg-primary/5" : "border-border"
            )}
            onClick={() => {
              if (!isDisabled) handleToggle(opt.value);
            }}
            data-testid={"difficulty-" + opt.value}
          >
            <div className="flex items-start justify-between">
              <div>
                <h4 className="font-semibold text-base">{opt.label}</h4>
                <p className="text-xs text-muted-foreground mt-1">{opt.desc}</p>        
              </div>
              {isSelected && (
                <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary ml-2">
                  <Check className="h-3 w-3 text-primary-foreground" />
                </div>
              )}
            </div>
            {isDisabled && <p className="text-[10px] text-destructive mt-2 font-medium">No questions available</p>}
          </Card>
          );
        })}
      </div>
    </div>
  );
}
